import { PublicClientApplication, type Configuration } from '@azure/msal-browser';
import { isEnvFlagTrue } from '@/config/env-flags';

const config: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
    authority: import.meta.env.VITE_MSAL_AUTHORITY,
    redirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI,
    postLogoutRedirectUri:
      import.meta.env.VITE_MSAL_POST_LOGOUT_REDIRECT_URI ?? import.meta.env.VITE_MSAL_REDIRECT_URI,
    // Stay on `redirectUri` (/frameredirect) after the AAD round-trip instead of
    // bouncing back to the URL that started login. Because login is initiated
    // FROM the `/login` page, MSAL's login-request URL is `/login`, so `true`
    // returned the user to `/login` after auth (a second login-screen flash)
    // before LoginPage detected the active account and forwarded to home
    // (PRTL-2603 scenario 02). The shell resumes deep links itself via the
    // stashed `request.url` replayed by FrameRedirectPage, so this is redundant.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // MSAL cache location. Mirrors legacy `msal-v3.config.ts`, which selects
    // sessionStorage vs localStorage from the `enableSessionStorage` feature
    // flag (legacy default: true → sessionStorage). Here it is driven by
    // `VITE_ENABLE_SESSION_STORAGE` with the same semantics.
    //
    // Default is localStorage: it survives tab reopen and is what the running
    // dev/canary sessions already use; set `VITE_ENABLE_SESSION_STORAGE=true`
    // to match legacy exactly (per-session, cleared when all tabs close — the
    // login hint then re-authenticates a fresh tab silently).
    cacheLocation: isEnvFlagTrue(import.meta.env.VITE_ENABLE_SESSION_STORAGE)
      ? 'sessionStorage'
      : 'localStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(config);

/**
 * Clears stale MSAL state from prior failed redirects.
 *
 * MSAL stores `interaction.status` in sessionStorage and refuses new
 * `loginRedirect` calls with `interaction_in_progress` until cleared.
 * The status key varies across MSAL versions (`msal.interaction.status` in
 * v2, prefixed with the clientId in v3+), so we sweep anything starting
 * with `msal.` that smells like a redirect-state key. Account keys (which
 * hold actual session data) are preserved.
 */
function clearStaleInteraction(): void {
  try {
    // Only sweep the storage location MSAL is configured to use. Sweeping
    // localStorage when MSAL uses sessionStorage fires cross-tab `storage`
    // events that cause MSAL instances in other tabs to drop their in-memory
    // active account (the "Not signed in on tab duplicate" bug).
    const primaryStorage = isEnvFlagTrue(import.meta.env.VITE_ENABLE_SESSION_STORAGE)
      ? sessionStorage
      : localStorage;
    const toRemove: string[] = [];
    for (let i = 0; i < primaryStorage.length; i++) {
      const k = primaryStorage.key(i);
      if (!k) continue;
      if (
        k === 'msal.interaction.status' ||
        k.endsWith('.interaction.status') ||
        k.endsWith('.request.params') ||
        k.endsWith('.request.state') ||
        k.includes('.redirect.')
      ) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) primaryStorage.removeItem(k);
  } catch {
    /* storage may be unavailable */
  }
}

/**
 * Must be awaited before rendering. Handles the redirect promise and selects
 * a default active account if one is already cached.
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();
  try {
    await msalInstance.handleRedirectPromise();
  } catch (err) {
    // Don't let a stale/failed redirect response block app boot — log and clear.
    console.error('[msal] handleRedirectPromise failed:', err);
    clearStaleInteraction();
  }

  // Always sweep stale interaction state after redirect handling — covers
  // the case where a previous `loginRedirect` aborted (e.g. AAD error page)
  // and left `interaction.status` in `in_progress`. Without this we'd loop
  // back to `interaction_in_progress` on the next route guard.
  if (!msalInstance.getActiveAccount()) {
    clearStaleInteraction();
  }

  if (!msalInstance.getActiveAccount()) {
    const [first] = msalInstance.getAllAccounts();
    if (first) msalInstance.setActiveAccount(first);
  }
}

// Exported so route guards can clear state if `loginRedirect` itself throws
// `interaction_in_progress` and we want to retry.
export { clearStaleInteraction };
